"use client";

import { useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { clsx } from "clsx";
import type { LucideIcon } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import ThemeSwitch from "@/components/ThemeSwitch";
import {
  Info,
  BadgeDollarSign,
  HelpCircle,
  Mail,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  NotebookPen,
  LogIn,
  UserPlus,
} from "lucide-react";
import NavigationBot from "@/components/NavigationBot";
import { Logo } from "@/components/Logo";

type NavItem = { href: string; label: string; icon: LucideIcon };

const NAV_GROUPS: { label: string; items: NavItem[] }[] = [
  {
    label: "Explore",
    items: [{ href: "/tools/notes", label: "Notes", icon: NotebookPen }],
  },
  {
    label: "Company",
    items: [
      { href: "/about", label: "About", icon: Info },
      { href: "/pricing", label: "Pricing", icon: BadgeDollarSign },
      { href: "/faq", label: "FAQ", icon: HelpCircle },
      { href: "/contact", label: "Contact", icon: Mail },
    ],
  },
];

interface GuestNavProps {
  collapsed?: boolean;
  onToggle?: () => void;
}

export function GuestNav({ collapsed = false, onToggle }: GuestNavProps) {
  const pathname = usePathname();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);

  const isActive = (href: string) =>
    pathname === href || (href !== "/" && pathname.startsWith(href));

  return (
    <>
      {/* Mobile & Tablet: top bar + drawer.
          Wrapped in `lg:hidden` so the mobile chrome is CSS-deterministic and a
          guest can NEVER be stuck on the desktop sidebar layout regardless of
          JS viewport-state timing (SSR/hydration/tablet breakpoint edge cases). */}
      <div className="lg:hidden">
        <nav className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur-md">
          <div className="flex h-14 items-center justify-between gap-2 px-4">
            <Link
              href="/"
              className="flex items-center transition-opacity hover:opacity-70"
            >
              <Logo className="h-10 w-auto" />
            </Link>
            <ThemeSwitch />
            <div className="flex items-center gap-2">
              {!user && (
                <Link href="/auth/signup">
                  <Button
                    size="sm"
                    className="rounded-full bg-foreground text-background font-medium text-xs"
                  >
                    Register
                  </Button>
                </Link>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-11 w-11"
                onClick={() => setOpen((p) => !p)}
                aria-label="Toggle menu"
                aria-expanded={open}
              >
                {open ? <X size={20} /> : <Menu size={20} />}
              </Button>
            </div>
          </div>

          {open && (
            <div className="space-y-1 border-t border-border bg-background px-4 py-3 overflow-y-auto max-h-[calc(100vh-3.5rem)]">
              {NAV_GROUPS.flatMap((g) => g.items).map((item) => {
                const Icon = item.icon;
                const active = isActive(item.href);
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    onClick={() => setOpen(false)}
                    className={clsx(
                      "flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors",
                      active
                        ? "bg-y text-black"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground",
                    )}
                  >
                    <Icon size={18} className="shrink-0" />
                    {item.label}
                  </Link>
                );
              })}
              <div className="my-2 rounded-xl border border-border bg-background">
                <NavigationBot variant="sidebar" />
              </div>
              <div className="my-2 h-px bg-border" />
              {user ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    logout();
                    setOpen(false);
                  }}
                  className="w-full justify-start text-sm font-medium"
                >
                  Logout
                </Button>
              ) : (
                <Link href="/auth/login" onClick={() => setOpen(false)}>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full rounded-full"
                  >
                    Log in
                  </Button>
                </Link>
              )}
            </div>
          )}
        </nav>
      </div>

      {/* Tablet & Desktop: collapsible sidebar.
          Gated with `hidden lg:flex` so it only appears at >=lg; below lg the
          mobile top bar (lg:hidden above) is the only nav — a guest can never
          be stuck on the desktop sidebar regardless of JS viewport timing. */}
      <nav
        className={clsx(
          "hidden lg:flex fixed top-0 left-0 z-50 h-screen flex-col border-r border-border bg-background/95 backdrop-blur-md transition-[width] duration-300 ease-spring",
          "w-16",
          collapsed ? "xl:w-16" : "xl:w-64",
        )}
      >
        {/* Brand + collapse toggle */}
        <div className="flex h-16 items-center justify-between gap-2 border-b border-border px-3">
          {!collapsed && (
            <Link
              href="/"
              className="hidden xl:flex min-w-0 items-center transition-opacity hover:opacity-70"
            >
              <Logo className="h-10 w-auto" />
            </Link>
          )}
          <button
            type="button"
            onClick={onToggle}
            aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
            aria-expanded={!collapsed}
            className="ml-auto hidden h-9 w-9 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground xl:flex"
          >
            {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </button>
        </div>

        {/* Sectioned nav */}
        <div className="scrollbar-hide flex-1 space-y-5 overflow-y-auto overflow-x-hidden px-2 py-4">
          {NAV_GROUPS.map((group) => (
            <div key={group.label} className="space-y-1">
              {!collapsed && (
                <p className="hidden px-3 pb-1 text-xs font-black uppercase tracking-[0.2em] text-muted-foreground/70 xl:block">
                  {group.label}
                </p>
              )}
              {group.items.map((item) => {
                const active = isActive(item.href);
                const Icon = item.icon;
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    title={collapsed ? item.label : undefined}
                    className={clsx(
                      "group relative flex items-center justify-center gap-3 rounded-xl px-0 py-2.5 text-sm font-medium transition-colors",
                      active
                        ? "bg-y text-black shadow-[0_0_18px_rgba(255,214,0,0.25)]"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground",
                      !collapsed && "xl:justify-start xl:px-3",
                    )}
                  >
                    <Icon size={20} className="shrink-0" />
                    {!collapsed && (
                      <span className="hidden xl:inline">{item.label}</span>
                    )}
                    {collapsed && (
                      <span className="pointer-events-none absolute left-full z-[200] ml-3 whitespace-nowrap rounded-md bg-foreground px-2 py-1 text-xs font-bold text-background opacity-0 transition-opacity group-hover:opacity-100">
                        {item.label}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          ))}
        </div>

        {/* Auth actions (guests get Sign in / Register; signed-in users get Logout) */}
        <div className="border-t border-border px-2 pt-3 pb-2">
          {!user ? (
            <div className="flex flex-col gap-2 xl:flex-row">
              <Link
                href="/auth/login"
                title={collapsed ? "Log in" : undefined}
                className={clsx(
                  "flex flex-1 items-center justify-center gap-2 rounded-xl border border-border px-0 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
                  !collapsed && "xl:justify-start xl:px-3",
                )}
              >
                <LogIn size={18} className="shrink-0" />
                {!collapsed && <span className="hidden xl:inline">Log in</span>}
              </Link>
              <Link
                href="/auth/signup"
                title={collapsed ? "Register" : undefined}
                className={clsx(
                  "flex flex-1 items-center justify-center gap-2 rounded-xl bg-y px-0 py-2.5 text-sm font-bold text-black shadow-[0_0_18px_rgba(255,214,0,0.25)] transition-opacity hover:opacity-90",
                  !collapsed && "xl:justify-start xl:px-3",
                )}
              >
                <UserPlus size={18} className="shrink-0" />
                {!collapsed && (
                  <span className="hidden xl:inline">Register</span>
                )}
              </Link>
            </div>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={logout}
              className={clsx(
                "w-full justify-center text-sm font-medium xl:justify-start",
                collapsed && "justify-center",
              )}
            >
              <LogIn size={18} className="shrink-0" />
              {!collapsed && (
                <span className="hidden ml-2 xl:inline">Logout</span>
              )}
            </Button>
          )}
        </div>

        {/* Command palette trigger */}
        <div className="px-2 pb-3 pt-2">
          <NavigationBot variant="sidebar" collapsed={collapsed} />
        </div>
      </nav>
    </>
  );
}
